import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@iconify/icons-mdi/delete";
import FileDocumentIcon from "@iconify/icons-mdi/file-document";
import TextBoxIcon from "@iconify/icons-mdi/text-box";
import { InlineIcon } from "@iconify/react";
import axios from "axios";
import {
  OcCard,
  OcCardButtonBar,
  OcDropzoneArea,
  OcFormSwitch,
  OcPageTransition,
} from "oc/components";
import {
  useOcApi,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";
import { getFormatTextWidthValue } from "oc/utils";

type MyDocumentsUploadFilesProps = {
  setShowNewCard: (show: boolean) => void;
  contractCode: string;
};

export const MyDocumentsUploadFiles = ({
  setShowNewCard,
  contractCode,
}: MyDocumentsUploadFilesProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useOcIsMobile();

  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [userView, setUserView] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [axiosError, setAxiosError] = useState(false);
  const { setOpenMessage } = useOcSnackbar();
  let {
    contractCode: uploaderContractCode,
    employeeCode: uploaderEmployeeCode,
  } = useOcCurrentUserState();

  const handleDropzoneChange = (file: File | File[]) => {
    let selectedFile: File | null = null;
    if (Array.isArray(file)) {
      selectedFile = file.length > 0 ? file[0] : null;
    } else {
      selectedFile = file;
    }
    if (selectedFile?.size && selectedFile.size > 9000000) {
      setOpenMessage({
        type: "error",
        message: t("OC_FILE_UPLOAD_SIZE_FAILED"),
      });
      return;
    }
    setFile(selectedFile);
  };

  const { apiUrl } = useOcApi();
  const url = `${apiUrl}/upload-document`;

  const handleUpload = async () => {
    //A MyDocuments V1-ben nem terveztünk csoportosan feltölteni dokumentumokat.
    //Ha megvalósítsuk, akkor a [contractCode] rész módosítára vár!
    //TODO
    const users = [contractCode];

    const formData = new FormData();
    formData.append("description", description);
    formData.append("type", "document");
    formData.append("userView", userView.toString());
    formData.append("uploaderContractCode", uploaderContractCode);
    formData.append("uploaderEmployeeCode", uploaderEmployeeCode);
    formData.append("users", JSON.stringify(users));
    if (file) {
      formData.append("fileSize", file.size.toString());
      formData.append("file", file);
    }

    try {
      const config = {
        onUploadProgress: (
          progressEvent: import("axios").AxiosProgressEvent,
        ) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded! * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      };
      await axios.post(url, formData, config);
      setOpenMessage({
        type: "success",
        message: t("MY_DOCUMENTS_DOCUMENT_UPLOAD_SUCCESSFUL"),
      });
      setShowNewCard(false);
    } catch (error) {
      setAxiosError(true);
      setOpenMessage({
        type: "error",
        message: t("MY_DOCUMENTS_DOCUMENT_UPLOAD_FAILED"),
      });
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleCancel = () => {
    setFile(null);
    setShowNewCard(false);
  };

  return (
    <OcPageTransition>
      <OcCard label={t("MY_DOCUMENTS_UPLOAD_DOCUMENT")}>
        <Box height="16px" />
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
        >
          {!file && (
            <Box width={isMobile ? "100%" : "500px"}>
              <Collapse in={true} timeout="auto" id="dropzone-area">
                <OcDropzoneArea
                  dropzoneText={t("OC_DRAG_AND_DROP_FILE")}
                  acceptedFiles={{
                    "application/*": [".pdf", ".doc", ".docx", ".xls", ".xlsx"],
                    "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                  }}
                  onChange={handleDropzoneChange}
                  height="81px"
                />
              </Collapse>
            </Box>
          )}
          {!uploadProgress && (
            <Collapse
              in={file ? true : false}
              style={{ width: isMobile ? "100%" : "auto" }}
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                style={{
                  width: isMobile ? "auto" : "459px",
                  backgroundColor: theme.palette.gray20.main,
                  padding: "16px 20px",
                  borderRadius: "8px",
                  border: "1px solid #C6CACC",
                }}
              >
                <Box>
                  <InlineIcon
                    icon={TextBoxIcon}
                    width="24px"
                    style={{ color: "#4d4d4d" }}
                  />
                </Box>
                <Box sx={{ width: "100%", padding: "0px 16px" }}>
                  <Typography variant="body1">
                    {isMobile
                      ? getFormatTextWidthValue(file?.name ?? "", 20)
                      : file?.name}
                  </Typography>
                </Box>
                <Box>
                  <InlineIcon
                    id="delete-icon"
                    icon={DeleteIcon}
                    width="24px"
                    style={{
                      color: "#4d4d4d",
                      cursor: "pointer",
                    }}
                    onClick={removeFile}
                  />
                </Box>
              </Box>
            </Collapse>
          )}
          <Collapse
            in={uploadProgress > 1 ? true : false}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              style={{
                width: isMobile ? "auto" : "459px",
                backgroundColor: theme.palette.gray20.main,
                padding: "16px 20px",
                borderRadius: "8px",
                border: axiosError ? "1px solid #CC0033" : "1px solid #C6CACC",
              }}
            >
              <Box>
                <InlineIcon
                  icon={FileDocumentIcon}
                  width="24px"
                  style={{ color: "#4d4d4d" }}
                />
              </Box>
              <Box
                sx={{
                  width: "100%",
                  padding: "0px 16px",
                }}
              >
                <Typography variant="body1">
                  {isMobile
                    ? getFormatTextWidthValue(file?.name ?? "", 20)
                    : file?.name}
                </Typography>
                {!axiosError ? (
                  <>
                    <Box padding="16px 0px">
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        color="primary"
                        style={{ borderRadius: "8px" }}
                      />
                    </Box>
                    <Typography variant="caption" style={{ color: "#919699" }}>
                      {uploadProgress}%
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#CC0033",
                      marginTop: "2px",
                    }}
                  >
                    {t("OC_UPLOAD_FAILED")}
                  </Typography>
                )}
              </Box>
              <Box>
                <InlineIcon
                  id="delete-icon"
                  icon={DeleteIcon}
                  width="24px"
                  style={{
                    color: "#4d4d4d",
                    cursor: "pointer",
                  }}
                  onClick={removeFile}
                />
              </Box>
            </Box>
          </Collapse>
          <Box height="32px" />
          <TextField
            id="designation"
            value={description}
            variant="outlined"
            label={t("OC_DESIGNATION")}
            type="string"
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: isMobile ? "100%" : "500px" }}
          />
        </Box>
        <Box height="32px" />
        <Box display="flex" alignItems="center" justifyContent="center">
          <Box display="flex" alignItems="center" width="500px">
            <OcFormSwitch
              id="employeeVisibility"
              checked={userView}
              inputProps={{ "aria-label": "controlled" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUserView(e.target.checked)
              }
              disabled={false}
            />
            <Box width="8px" />
            <Typography variant="body1">
              {t("MY_DOCUMENTS_EMPLOYEE_VISIBILITY")}
            </Typography>
          </Box>
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonColor="secondary"
          rightButtonText={t("OC_CANCEL")}
          handlePrimaryButton={handleUpload}
          primaryButtonText={t("OC_UPLOAD")}
          primaryButtonDisabled={!file || !description}
        />
        <Box height="16px" />
      </OcCard>
    </OcPageTransition>
  );
};

export default MyDocumentsUploadFiles;
